import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  createT,
  formatAmount,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatMoney,
  formatNumber,
  type Locale,
  type Params,
} from "@hik/i18n";

interface I18n {
  locale: Locale;
  t: (key: string, params?: Params) => string;
  date: (d: string, style?: Parameters<typeof formatDate>[2]) => string;
  range: (a: string, b: string) => string;
  dateTime: (iso: string) => string;
  amount: (n: number, unit: "day" | "hour") => string;
  money: (minor: number, currency?: string) => string;
  number: (n: number) => string;
  /** "2 gündür", "3 saattir" gibi bekleme süresi. */
  since: (iso: string) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ locale, timezone, children }: { locale: Locale; timezone: string; children: ReactNode }) {
  const value = useMemo<I18n>(() => {
    const t = createT(locale);
    return {
      locale,
      t,
      date: (d, style) => formatDate(locale, d, style),
      range: (a, b) => formatDateRange(locale, a, b),
      dateTime: (iso) => formatDateTime(locale, iso, timezone),
      amount: (n, unit) => formatAmount(locale, n, unit),
      money: (minor, currency = "TRY") => formatMoney(locale, minor, currency),
      number: (n) => formatNumber(locale, n),
      since: (iso) => {
        const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
        if (mins < 60) return t("ui.time.minutes", { count: Math.max(1, mins) });
        const hours = Math.round(mins / 60);
        if (hours < 24) return t("ui.time.hours", { count: hours });
        return t("ui.time.days", { count: Math.round(hours / 24) });
      },
    };
  }, [locale, timezone]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error("I18nProvider missing");
  return v;
}

export function useT() {
  return useI18n().t;
}
